import { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    ExternalLink,
    LogOut,
    Menu,
    X,
    ChevronDown,
    Search,
    Pencil,
    Eye,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";
import { api } from "@/lib/api";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { ITEM_CATALOG, FALLBACK_LAYOUT } from "@/lib/navCatalog";

const STORAGE_KEY = "clientops:nav:open-groups";

function _readSavedOpen() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

function _groupKeyForPath(layout, pathname) {
    for (const g of layout.groups || []) {
        if ((g.items || []).some((key) => {
            const it = ITEM_CATALOG[key];
            if (!it) return false;
            return it.to === pathname || (it.to !== "/app" && pathname.startsWith(it.to + "/"));
        })) {
            return g.key;
        }
    }
    if (pathname === "/app" || pathname === "/app/") return "overview";
    return null;
}

function GroupHeader({ label, open, onToggle, testId }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-2 px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-[#7A7F8A] hover:text-[#0F1115] transition-colors"
            data-testid={testId}
            aria-expanded={open}
        >
            <span>{label}</span>
            <ChevronDown className={"h-3 w-3 transition-transform duration-200 " + (open ? "" : "-rotate-90")} />
        </button>
    );
}

function NavItem({ item, badgeVal, onClick, testIdPrefix = "nav" }) {
    const Icon = item.icon;
    return (
        <NavLink
            key={item.key}
            to={item.to}
            end={item.end}
            onClick={onClick}
            data-testid={`${testIdPrefix}-${item.key}`}
            className={({ isActive }) =>
                `flex items-center justify-between gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                        ? "bg-[#0F1115] text-white"
                        : "text-[#5E636E] hover:bg-zinc-100 hover:text-[#0F1115]"
                }`
            }
        >
            <span className="flex items-center gap-2.5">
                <Icon className="h-4 w-4" />
                {item.label}
            </span>
            {badgeVal > 0 && (
                <span
                    className="inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold min-w-[18px] h-[18px] px-1.5"
                    data-testid={`${testIdPrefix}-badge-${item.key}`}
                >
                    {badgeVal}
                </span>
            )}
        </NavLink>
    );
}

export default function AppLayout() {
    const { user, tenant, logout } = useAuth();
    const { has: hasCap } = useCapabilities();
    const nav = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState(false); // mobile drawer
    const [counts, setCounts] = useState({ unmatched_count: 0, open_work_count: 0, tax_check_count: 0 });
    const [layout, setLayout] = useState(FALLBACK_LAYOUT);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const previewAs = searchParams.get("preview_as");
    const isAdmin = user?.role === "firm_admin";

    // Fetch the role-filtered layout from the backend; refetch when
    // preview_as changes so admins can preview the Staff sidebar.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const url = previewAs && isAdmin ? `/nav/layout?preview_as=${previewAs}` : "/nav/layout";
                const { data } = await api.get(url);
                if (!cancelled && data?.groups) setLayout(data);
            } catch { /* keep fallback */ }
        })();
        return () => { cancelled = true; };
    }, [previewAs, isAdmin]);

    // Build the list of groups the user may see. Overview (first group,
    // key='overview') is pinned/no-collapse.
    const navGroups = useMemo(() => {
        return (layout.groups || []).map((g) => ({
            ...g,
            pinned: g.key === "overview",
            items: (g.items || []).map((k) => ({ key: k, ...ITEM_CATALOG[k] })).filter((it) => it.to),
        })).filter((g) => g.items.length > 0);
    }, [layout]);

    const [groupOpen, setGroupOpen] = useState(() => {
        const saved = _readSavedOpen();
        return saved || {};
    });

    useEffect(() => {
        const active = _groupKeyForPath(layout, location.pathname);
        setGroupOpen((prev) => {
            const next = { ...prev };
            for (const g of navGroups) {
                if (g.pinned) continue;
                if (!(g.key in next)) next[g.key] = g.key === active;
            }
            if (active && active !== "overview" && !next[active]) next[active] = true;
            return next;
        });
    }, [navGroups, layout, location.pathname]);

    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(groupOpen)); } catch { /* ignore */ }
    }, [groupOpen]);

    function toggleGroup(key) {
        setGroupOpen((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    useEffect(() => {
        let cancelled = false;
        async function loadCounts() {
            try {
                const [u, w, tp] = await Promise.all([
                    api.get("/sync/unmatched"),
                    api.get("/work-queue/stats").catch(() => ({ data: null })),
                    api.get("/tax-position/badge").catch(() => ({ data: null })),
                ]);
                if (cancelled) return;
                const breached = w.data ? (w.data.breached || 0) : 0;
                setCounts({
                    unmatched_count: Array.isArray(u.data) ? u.data.length : 0,
                    open_work_count: breached,
                    tax_check_count: tp.data ? (tp.data.count || 0) : 0,
                });
            } catch { /* ignore */ }
        }
        loadCounts();
        const t = setInterval(loadCounts, 60000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    // Ctrl+K / ⌘K opens the command palette.
    useEffect(() => {
        function onKey(e) {
            if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
            if (e.key === "Escape") setPaletteOpen(false);
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    async function handleLogout() {
        await logout();
        nav("/", { replace: true });
    }

    const groupBadgeTotals = useMemo(() => {
        const totals = {};
        for (const g of navGroups) {
            totals[g.key] = g.items.reduce((sum, it) => sum + (it.badge ? counts[it.badge] || 0 : 0), 0);
        }
        return totals;
    }, [navGroups, counts]);

    function renderGroup(g, { mobile }) {
        const testIdPrefix = mobile ? "mobile-nav" : "nav";
        if (g.pinned) {
            return (
                <div key={g.key} className="space-y-0.5">
                    {g.items.map((item) => (
                        <NavItem
                            key={item.key}
                            item={item}
                            badgeVal={item.badge ? counts[item.badge] : 0}
                            onClick={mobile ? () => setOpen(false) : undefined}
                            testIdPrefix={testIdPrefix}
                        />
                    ))}
                </div>
            );
        }
        const isOpen = groupOpen[g.key];
        const groupBadge = groupBadgeTotals[g.key] || 0;
        return (
            <div key={g.key}>
                <div className="flex items-center">
                    <GroupHeader
                        label={g.label}
                        open={isOpen}
                        onToggle={() => toggleGroup(g.key)}
                        testId={`${testIdPrefix}-group-${g.key}`}
                    />
                    {!isOpen && groupBadge > 0 && (
                        <span
                            className="mr-3 inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold min-w-[18px] h-[18px] px-1.5"
                            data-testid={`${testIdPrefix}-group-badge-${g.key}`}
                        >
                            {groupBadge}
                        </span>
                    )}
                </div>
                {isOpen && (
                    <div className="space-y-0.5 mt-0.5" data-testid={`${testIdPrefix}-group-items-${g.key}`}>
                        {g.items.map((item) => (
                            <NavItem
                                key={item.key}
                                item={item}
                                badgeVal={item.badge ? counts[item.badge] : 0}
                                onClick={mobile ? () => setOpen(false) : undefined}
                                testIdPrefix={testIdPrefix}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F7F7F8] flex">
            {previewAs && isAdmin && (
                <div className="fixed top-0 inset-x-0 z-[60] bg-amber-100 border-b border-amber-300 text-amber-900 text-[12px] px-4 py-1.5 flex items-center justify-between" data-testid="preview-banner">
                    <span className="flex items-center gap-2">
                        <Eye className="h-3.5 w-3.5" /> Previewing sidebar as <b>{previewAs.replace("_", " ")}</b>
                    </span>
                    <button
                        onClick={() => { const p = new URLSearchParams(searchParams); p.delete("preview_as"); setSearchParams(p); }}
                        className="underline text-[11.5px] hover:text-amber-700"
                        data-testid="preview-exit"
                    >
                        Exit preview
                    </button>
                </div>
            )}
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-[#E4E4E7] bg-white">
                <div className="px-6 py-6">
                    <Brand size="md" to="/app" />
                </div>
                <div className="px-3 pb-2">
                    <button
                        type="button"
                        onClick={() => setPaletteOpen(true)}
                        className="w-full flex items-center gap-2 rounded-md border border-[#E4E4E7] bg-[#FAFAFA] px-2.5 py-1.5 text-[12px] text-[#5E636E] hover:bg-white hover:text-[#0F1115] transition-colors"
                        data-testid="sidebar-search"
                    >
                        <Search className="h-3.5 w-3.5" />
                        <span className="flex-1 text-left">Search tools…</span>
                        <kbd className="rounded border border-[#E4E4E7] bg-white px-1.5 py-0.5 text-[10px] font-mono text-[#5E636E]">Ctrl K</kbd>
                    </button>
                </div>
                <div className="px-3 pb-3">
                    <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2">
                        <div className="co-label">Firm</div>
                        <div className="mt-1 truncate text-sm font-medium" data-testid="sidebar-tenant-name">{tenant?.name || "—"}</div>
                        <div className="mt-0.5 text-[11px] text-[#5E636E]" data-testid="sidebar-tenant-country">
                            New Zealand · IRD / GST
                        </div>
                    </div>
                    {hasCap("settings.edit_nav_layout") && (
                        <button
                            type="button"
                            onClick={() => nav("/app/settings/navigation-layout")}
                            className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md border border-[#E4E4E7] bg-white px-2 py-1.5 text-[11.5px] text-[#5E636E] hover:bg-zinc-50 hover:text-[#0F1115] transition-colors"
                            data-testid="sidebar-edit-layout"
                        >
                            <Pencil className="h-3 w-3" />
                            Edit layout
                        </button>
                    )}
                </div>
                <nav className="flex-1 overflow-y-auto px-3 pb-6" data-testid="sidebar-nav">
                    {navGroups.map((g) => renderGroup(g, { mobile: false }))}
                    {tenant?.practice_manager_url && (
                        <a
                            href={tenant.practice_manager_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-4 flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 hover:bg-emerald-100 transition-colors"
                            data-testid="sidebar-pm-link"
                        >
                            <span className="flex items-center gap-2 min-w-0">
                                <span className="grid h-5 w-5 place-items-center rounded bg-emerald-600 text-white text-[10px] font-bold shrink-0">P</span>
                                <span className="truncate">{tenant.practice_manager_label || "Practice Manager"}</span>
                            </span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                        </a>
                    )}
                </nav>
                <div className="border-t border-[#E4E4E7] p-3">
                    <div className="rounded-md bg-[#FAFAFA] border border-[#E4E4E7] px-3 py-2.5">
                        <div className="truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name}</div>
                        <div className="truncate text-[11px] text-[#5E636E]">{user?.email}</div>
                        <div className="mt-2 flex items-center justify-between">
                            <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                                {user?.role?.replace("_", " ")}
                            </span>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115] transition-colors"
                                data-testid="sidebar-logout-btn"
                            >
                                <LogOut className="h-3.5 w-3.5" />
                                Sign out
                            </button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex h-14 items-center justify-between border-b border-[#E4E4E7] bg-white/85 backdrop-blur px-4">
                <Brand size="sm" to="/app" />
                <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-open">
                    <Menu className="h-5 w-5" />
                </Button>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setOpen(false)}>
                    <div
                        className="absolute left-0 top-0 h-full w-72 bg-white p-4 flex flex-col overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="mobile-drawer"
                    >
                        <div className="flex items-center justify-between">
                            <Brand size="md" to="/app" />
                            <Button variant="ghost" size="icon" onClick={() => setOpen(false)} data-testid="mobile-menu-close">
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                        <div className="mt-4 text-sm font-medium">{tenant?.name}</div>
                        <div className="text-[11px] text-[#5E636E]">New Zealand</div>
                        <nav className="mt-4 flex-1">
                            {navGroups.map((g) => renderGroup(g, { mobile: true }))}
                            {tenant?.practice_manager_url && (
                                <a
                                    href={tenant.practice_manager_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => setOpen(false)}
                                    className="mt-3 flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                                    data-testid="mobile-pm-link"
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        <span className="grid h-5 w-5 place-items-center rounded bg-emerald-600 text-white text-[10px] font-bold">P</span>
                                        <span className="truncate">{tenant.practice_manager_label || "Practice Manager"}</span>
                                    </span>
                                    <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                            )}
                        </nav>
                        <button
                            onClick={handleLogout}
                            className="mt-4 flex items-center gap-2 rounded-md border border-[#E4E4E7] px-3 py-2 text-sm hover:bg-zinc-50"
                            data-testid="mobile-logout-btn"
                        >
                            <LogOut className="h-4 w-4" /> Sign out
                        </button>
                    </div>
                </div>
            )}

            <main className="flex-1 min-w-0 pt-14 lg:pt-0">
                <div className={`mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10 ${previewAs && isAdmin ? "pt-14 lg:pt-16" : ""}`}>
                    <Outlet />
                </div>
            </main>

            {paletteOpen && <NavSearchPalette onClose={() => setPaletteOpen(false)} groups={navGroups} />}
        </div>
    );
}

/* -------- Ctrl+K search palette -------- */
function NavSearchPalette({ onClose, groups }) {
    const [q, setQ] = useState("");
    const nav = useNavigate();
    const flat = useMemo(() => {
        const out = [];
        for (const g of groups) {
            for (const it of g.items) {
                out.push({ ...it, group: g.label });
            }
        }
        return out;
    }, [groups]);
    const results = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return flat.slice(0, 20);
        return flat.filter((it) => it.label.toLowerCase().includes(term) || it.key.includes(term)).slice(0, 20);
    }, [flat, q]);
    useEffect(() => {
        const el = document.getElementById("nav-palette-input");
        if (el) el.focus();
    }, []);
    return (
        <div className="fixed inset-0 z-[70] bg-black/30 flex items-start justify-center pt-24" onClick={onClose} data-testid="nav-palette">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-[#E4E4E7] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 border-b border-[#E4E4E7] px-3 py-2">
                    <Search className="h-4 w-4 text-[#5E636E]" />
                    <input id="nav-palette-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tools…" className="flex-1 outline-none text-[14px]" data-testid="nav-palette-input" />
                    <kbd className="rounded border border-[#E4E4E7] bg-[#FAFAFA] px-1.5 py-0.5 text-[10px] font-mono text-[#5E636E]">Esc</kbd>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {results.length === 0 && (
                        <div className="px-4 py-6 text-center text-[13px] text-[#5E636E]">No matches</div>
                    )}
                    {results.map((it) => {
                        const Icon = it.icon;
                        return (
                            <button
                                key={it.key}
                                onClick={() => { nav(it.to); onClose(); }}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-zinc-50 border-b border-[#F3F3F3]"
                                data-testid={`nav-palette-${it.key}`}
                            >
                                <span className="flex items-center gap-2 min-w-0">
                                    <Icon className="h-4 w-4 text-[#5E636E] shrink-0" />
                                    <span className="truncate text-[13.5px]">{it.label}</span>
                                </span>
                                <span className="text-[10.5px] text-[#5E636E] uppercase tracking-wider">{it.group}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
